import { CaretRight } from "@phosphor-icons/react";
// eslint-disable-next-line import/named
import { Link } from "@tanstack/react-router";
import cx from "classnames";
import { type ReactNode } from "react";

/**
 * Shared breadcrumb-header type scale (size + weight + line height). The Ask
 * Puntt chat and project headers standardized on this, and the tickets header is
 * aligned to it so breadcrumb headers stay visually cohesive across the app:
 * 14px / medium / 20px line box.
 */
export const BREADCRUMB_HEADER_LINE_CLASS = "leading-5";

export const BREADCRUMB_HEADER_TEXT_CLASS = cx(
  "text-sm font-medium",
  BREADCRUMB_HEADER_LINE_CLASS,
);

/**
 * Shared treatment for the FINAL (title) breadcrumb segment — the chat/project
 * title and the tickets ticket title. Centralized here so all three breadcrumb
 * headers render the title at exactly the same size / weight / color and cannot
 * drift apart: 14px / medium / neutral-gray-11.
 */
export const BREADCRUMB_TITLE_TEXT_CLASS = cx(
  BREADCRUMB_HEADER_TEXT_CLASS,
  "text-puntt-neutral-gray-11",
);

/** Optical nudge so the caret matches the text's felt weight (bp cap/descender box). */
export const BREADCRUMB_HEADER_CARET_CLASS =
  "relative top-[2px] shrink-0 text-puntt-neutral-gray-9";

/**
 * Row wrapper for breadcrumb segments (root link, caret, title, chips). Keeps
 * every text node on one baseline instead of vertically centering mismatched boxes.
 */
export const BREADCRUMB_HEADER_TRAIL_CLASS =
  "flex min-w-0 items-baseline gap-1 overflow-hidden";

/** Trailing skill/owner chips beside breadcrumb text — baseline-aligned, not centered. */
export const BREADCRUMB_HEADER_CHIP_CLASS = "shrink-0 leading-5";

interface BreadcrumbHeaderRootProps {
  /** Route/path the root segment links to (the section landing page). */
  to: string;
  /** Visible label, doubling as the link's accessible name. */
  label: string;
  /** Stable testid for the root link. */
  testId: string;
  /**
   * Render the trailing caret separator. Omit when there is no following
   * breadcrumb segment (e.g. a chat with no title yet) so no dangling caret
   * shows.
   */
  showSeparator?: boolean;
}

export function BreadcrumbHeaderTrail({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(BREADCRUMB_HEADER_TRAIL_CLASS, className)}
      data-testid="breadcrumb-header-trail"
    >
      {children}
    </div>
  );
}

/**
 * The leading segment of a breadcrumb header — a link to the section root
 * followed by a decorative caret separator. Shared by the Ask Puntt chat and
 * project headers so both render an identical `Root › <title>` breadcrumb at
 * the same {@link BREADCRUMB_HEADER_TEXT_CLASS} type scale; only the root label
 * ("Ask Puntt" vs "All projects") and the trailing title segment differ.
 */
export function BreadcrumbHeaderRoot({
  to,
  label,
  testId,
  showSeparator = true,
}: BreadcrumbHeaderRootProps) {
  return (
    <span className="inline-flex shrink-0 items-baseline gap-1">
      <Link to={to} className="puntt-link shrink-0" data-testid={testId}>
        <span
          className={cx(
            BREADCRUMB_HEADER_TEXT_CLASS,
            "text-puntt-accent-11 hover:underline",
          )}
        >
          {label}
        </span>
      </Link>
      {showSeparator && (
        <CaretRight
          size={12}
          aria-hidden="true"
          data-testid="breadcrumb-header-caret"
          className={BREADCRUMB_HEADER_CARET_CLASS}
        />
      )}
    </span>
  );
}
